import React, { useState, useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  PlusIcon,
  ClockIcon,
  UserIcon,
  VideoCameraIcon,
  HomeIcon,
  MapPinIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

interface Lesson {
  id: string;
  studentName: string;
  instrument: string;
  date: string;
  time: string;
  duration: number;
  type: 'In-person' | 'Online';
  status: 'confirmed' | 'pending' | 'cancelled';
  googleEventId?: string;
}

interface SuggestedSlot {
  date: string;
  time: string;
  duration: number;
  reason: string;
}

interface CalendarData {
  teacherName: string;
  currentWeek: {
    startDate: string;
    endDate: string;
  };
  lessons: Lesson[];
  availability: Record<string, string[]>;
  suggestedSlots: SuggestedSlot[];
  googleCalendarSync: {
    connected: boolean;
    lastSync: string;
    syncStatus: string;
    eventsCreated: number;
    eventsUpdated: number;
  };
  weeklyStats: {
    totalLessons: number;
    totalHours: number;
    totalEarnings: number;
    averageGapBetweenLessons: string;
  };
}

const TeacherCalendar: React.FC = () => {
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  // Load calendar data
  useEffect(() => {
    const loadCalendarData = async () => {
      try {
        console.log('📅 Loading calendar data...');
        const response = await fetch('http://localhost:5000/api/booking/calendar/demo');
        const result = await response.json();
        
        if (result.success) {
          setCalendarData(result.data);
          console.log('✅ Calendar data loaded:', result.data);
        }
      } catch (error) {
        console.error('❌ Error loading calendar:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCalendarData();
  }, [currentWeekOffset]);

  const getWeekDays = () => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dates = ['Jan 27', 'Jan 28', 'Jan 29', 'Jan 30', 'Jan 31', 'Feb 1', 'Feb 2'];
    return days.map((day, index) => ({ day, date: dates[index] }));
  };

  const getLessonsForDay = (dayIndex: number) => {
    if (!calendarData) return [];
    const targetDate = ['2025-01-27', '2025-01-28', '2025-01-29', '2025-01-30', '2025-01-31', '2025-02-01', '2025-02-02'][dayIndex];
    return calendarData.lessons.filter(lesson => lesson.date === targetDate);
  };

  const getSuggestedSlotsForDay = (dayIndex: number) => {
    if (!calendarData) return [];
    const targetDate = ['2025-01-27', '2025-01-28', '2025-01-29', '2025-01-30', '2025-01-31', '2025-02-01', '2025-02-02'][dayIndex];
    return calendarData.suggestedSlots.filter(slot => slot.date === targetDate);
  };

  const getAvailabilityForDay = (dayIndex: number) => {
    if (!calendarData) return [];
    const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return calendarData.availability[dayNames[dayIndex]] || [];
  };

  const formatTime = (time: string) => {
    return time;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    return type === 'Online' ? 
      <VideoCameraIcon className="w-4 h-4" /> : 
      <HomeIcon className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading calendar..." />
      </div>
    );
  }

  if (!calendarData) {
    return (
      <div className="text-center py-12">
        <CalendarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Calendar Not Available</h3>
        <p className="text-gray-600">Unable to load calendar data.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{calendarData.teacherName}'s Schedule</h2>
            <p className="text-blue-100">
              {calendarData.currentWeek.startDate} - {calendarData.currentWeek.endDate}
            </p>
          </div>
          
          {/* Google Calendar Sync Status */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              {calendarData.googleCalendarSync.connected ? (
                <CheckCircleSolid className="w-5 h-5 text-green-300" />
              ) : (
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-300" />
              )}
              <span className="text-sm">
                {calendarData.googleCalendarSync.connected ? 'Google Calendar Synced' : 'Not Connected'}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Weekly Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4 mt-4">
          <div className="bg-white/10 rounded-lg p-2 lg:p-3">
            <div className="text-lg lg:text-2xl font-bold">{calendarData.weeklyStats.totalLessons}</div>
            <div className="text-xs lg:text-sm text-blue-100">Total Lessons</div>
          </div>
          <div className="bg-white/10 rounded-lg p-2 lg:p-3">
            <div className="text-lg lg:text-2xl font-bold">{calendarData.weeklyStats.totalHours}h</div>
            <div className="text-xs lg:text-sm text-blue-100">Teaching Hours</div>
          </div>
          <div className="bg-white/10 rounded-lg p-2 lg:p-3">
            <div className="text-lg lg:text-2xl font-bold">${calendarData.weeklyStats.totalEarnings}</div>
            <div className="text-xs lg:text-sm text-blue-100">Weekly Earnings</div>
          </div>
          <div className="bg-white/10 rounded-lg p-2 lg:p-3">
            <div className="text-lg lg:text-2xl font-bold">{calendarData.weeklyStats.averageGapBetweenLessons}</div>
            <div className="text-xs lg:text-sm text-blue-100">Avg Gap</div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-3 lg:p-6">
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2 lg:gap-4">
          {getWeekDays().map((dayInfo, dayIndex) => {
            const lessons = getLessonsForDay(dayIndex);
            const suggestedSlots = getSuggestedSlotsForDay(dayIndex);
            const availability = getAvailabilityForDay(dayIndex);
            
            return (
              <div key={dayIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Day Header */}
                <div className="bg-gray-50 px-3 py-2 border-b">
                  <div className="font-semibold text-gray-900">{dayInfo.day}</div>
                  <div className="text-sm text-gray-600">{dayInfo.date}</div>
                </div>

                {/* Day Content */}
                <div className="p-2 lg:p-3 min-h-[200px] lg:min-h-[300px]">
                  {/* Availability */}
                  {availability.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs font-medium text-gray-500 mb-1">Available</div>
                      {availability.map((slot, index) => (
                        <div key={index} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded mb-1">
                          {slot}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Lessons */}
                  {lessons.map((lesson) => (
                    <div
                      key={lesson.id}
                      className={`mb-2 p-2 rounded-lg border ${getStatusColor(lesson.status)} cursor-pointer hover:shadow-md transition-shadow`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium text-sm">{lesson.time}</div>
                        <div className="flex items-center space-x-1">
                          {getTypeIcon(lesson.type)}
                          {lesson.googleEventId && (
                            <CheckCircleIcon className="w-3 h-3 text-green-600" />
                          )}
                        </div>
                      </div>
                      
                      <div className="text-sm font-medium">{lesson.studentName}</div>
                      <div className="text-xs text-gray-600">{lesson.instrument} • {lesson.duration}min</div>
                      
                      <div className="flex items-center justify-between mt-1">
                        <span className={`text-xs px-1 py-0.5 rounded ${getStatusColor(lesson.status)}`}>
                          {lesson.status}
                        </span>
                        <div className="text-xs text-gray-500">{lesson.type}</div>
                      </div>
                    </div>
                  ))}

                  {/* Suggested Slots */}
                  {suggestedSlots.map((slot, index) => (
                    <div
                      key={index}
                      className="mb-2 p-2 rounded-lg border-2 border-dashed border-green-300 bg-green-50 cursor-pointer hover:bg-green-100 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-medium text-sm text-green-800">{slot.time}</div>
                        <PlusIcon className="w-4 h-4 text-green-600" />
                      </div>
                      
                      <div className="text-xs text-green-700 font-medium">Suggested Slot</div>
                      <div className="text-xs text-green-600">{slot.duration}min available</div>
                      <div className="text-xs text-green-600 mt-1">{slot.reason}</div>
                    </div>
                  ))}

                  {/* Empty State */}
                  {lessons.length === 0 && suggestedSlots.length === 0 && availability.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      <ClockIcon className="w-8 h-8 mx-auto mb-2" />
                      <div className="text-sm">No lessons</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Smart Scheduling Insights */}
        <div className="mt-4 lg:mt-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-3 lg:p-4">
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center text-sm lg:text-base">
            <CheckCircleIcon className="w-4 h-4 lg:w-5 lg:h-5 text-green-600 mr-2" />
            Smart Scheduling Insights
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 lg:gap-4 text-xs lg:text-sm">
            <div className="bg-white rounded-lg p-3">
              <div className="font-medium text-green-800">Optimal Gaps</div>
              <div className="text-gray-600">Monday 5:30 PM slot eliminates gaps between lessons</div>
            </div>
            <div className="bg-white rounded-lg p-3">
              <div className="font-medium text-blue-800">Efficiency Boost</div>
              <div className="text-gray-600">Tuesday 1:00 PM creates perfect back-to-back schedule</div>
            </div>
            <div className="bg-white rounded-lg p-3">
              <div className="font-medium text-purple-800">Revenue Opportunity</div>
              <div className="text-gray-600">3 suggested slots could add $225 weekly income</div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 lg:mt-6 flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <button className="bg-blue-600 text-white px-3 lg:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 text-sm lg:text-base">
              <PlusIcon className="w-4 h-4" />
              <span>Add Lesson</span>
            </button>
            <button className="bg-green-600 text-white px-3 lg:px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 text-sm lg:text-base">
              <CalendarIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Sync Google Calendar</span>
              <span className="sm:hidden">Sync Calendar</span>
            </button>
          </div>
          
          <div className="text-xs lg:text-sm text-gray-600 text-center lg:text-right">
            Last synced: {new Date(calendarData.googleCalendarSync.lastSync).toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherCalendar; 